import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BookmarkButton from "../components/BookmarkButton";
import ShareButton from "../components/ShareButton";
import { useAudioPlayer } from "../context/AudioPlayerContext";
import { getEpisode, episodes } from "../mock";
import { getArticlesByCategory } from "../articlesData";
import SEO from "../components/SEO";
import { Play, Pause, ArrowLeft, Clock, Calendar, LinkIcon, Headphones, ChevronDown, FileText } from "lucide-react";

const EpisodePage = () => {
  const { id } = useParams();
  const episode = getEpisode(id);
  const [playing, setPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const { play, isCurrent, isPlaying } = useAudioPlayer();
  const listening = episode && isCurrent(episode.id) && isPlaying;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setPlaying(false);
  }, [id]);

  if (!episode) {
    return (
      <div className="bg-[#eceae5] min-h-screen">
        <Navbar solid />
        <div className="pt-44 pb-40 text-center">
          <h1 className="font-display text-4xl text-neutral-900">Episode not found</h1>
          <Link to="/episodes" className="font-sans-ui text-neutral-600 underline mt-4 inline-block">
            Back to episodes
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const nice = (c) => (c ? c.charAt(0) + c.slice(1).toLowerCase() : "");
  const related = episodes
    .filter((e) => e.category === episode.category && e.id !== episode.id)
    .slice(0, 3);
  const relatedArticles = getArticlesByCategory(episode.category).slice(0, 3);

  return (
    <div className="bg-[#eceae5] min-h-screen">
      <SEO title={episode.title} description={episode.description} image={episode.thumbnail} type="video.other" />
      <Navbar solid />

      <div className="max-w-[1100px] mx-auto px-6 pt-32 md:pt-40 pb-8">
        <Link
          to="/episodes"
          className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft size={15} /> All episodes
        </Link>
        <p className="font-sans-ui text-xs uppercase tracking-[0.2em] text-neutral-500 mt-6">
          {episode.number} · {nice(episode.category)}
        </p>
        <h1 className="font-display text-4xl md:text-6xl text-neutral-900 mt-3 leading-[1.05]" style={{ fontWeight: 700 }}>
          {episode.title}
        </h1>
        <p className="font-display italic text-neutral-600 text-xl mt-2">{episode.guest}</p>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-500 mr-2">
            <Clock size={15} /> {episode.duration}
          </span>
          <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-500 mr-2">
            <Calendar size={15} /> {episode.date}
          </span>
          <button
            onClick={() => play(episode)}
            className="inline-flex items-center gap-2 font-sans-ui text-sm px-5 py-3 rounded-full bg-neutral-900 text-white hover:bg-neutral-700 transition-colors"
          >
            {listening ? <Pause size={16} fill="currentColor" /> : <Headphones size={16} />}
            {listening ? "Playing\u2026" : "Listen"}
          </button>
          <BookmarkButton type="episode" id={episode.id} variant="inline" />
          <ShareButton title={episode.title} />
        </div>
      </div>

      {/* Player */}
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-neutral-900 shadow-xl">
          {playing ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${episode.youtubeId}?autoplay=1`}
              title={episode.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full">
              <img src={episode.thumbnail} alt={episode.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={30} className="text-neutral-900 ml-1" fill="currentColor" />
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1100px] mx-auto px-6 py-14 grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2">
          <h2 className="font-display text-3xl text-neutral-900" style={{ fontWeight: 700 }}>Show notes</h2>
          <div className="mt-5">
            {episode.showNotes.map((p) => (
              <p key={p} className="font-display text-lg text-neutral-700 leading-relaxed mb-5">{p}</p>
            ))}
          </div>

          {episode.keyTakeaways && episode.keyTakeaways.length > 0 && (
            <div className="mt-8 bg-white/60 border border-neutral-200 rounded-lg p-6" data-testid="key-takeaways">
              <h3 className="font-display text-xl text-neutral-900" style={{ fontWeight: 700 }}>Key takeaways</h3>
              <ul className="mt-4 flex flex-col gap-3">
                {episode.keyTakeaways.map((t, i) => (
                  <li key={t} className="flex gap-3 font-display text-lg text-neutral-800 leading-relaxed">
                    <span className="font-sans-ui text-sm text-neutral-400 tabular-nums pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h3 className="font-display text-2xl text-neutral-900 mt-10" style={{ fontWeight: 700 }}>Chapters</h3>
          <ul className="mt-4 divide-y divide-neutral-300 border-y border-neutral-300">
            {episode.chapters.map((c) => (
              <li key={`${c.time}-${c.title}`} className="flex items-center gap-4 py-3">
                <span className="font-sans-ui text-sm text-neutral-500 tabular-nums w-14">{c.time}</span>
                <span className="font-display text-lg text-neutral-800">{c.title}</span>
              </li>
            ))}
          </ul>

          <h3 className="font-display text-2xl text-neutral-900 mt-10" style={{ fontWeight: 700 }}>Links &amp; resources</h3>
          <ul className="mt-4 flex flex-col gap-3">
            {episode.links.map((l) => (
              <li key={`${l.label}-${l.url}`}>
                <a href={l.url} className="inline-flex items-center gap-2 font-display text-lg text-neutral-800 link-underline">
                  <LinkIcon size={16} className="text-neutral-500" /> {l.label}
                </a>
              </li>
            ))}
          </ul>

          {episode.transcript && episode.transcript.length > 0 && (
            <div className="mt-12" data-testid="transcript-section">
              <button
                onClick={() => setShowTranscript((v) => !v)}
                data-testid="transcript-toggle"
                className="w-full flex items-center justify-between gap-3 border-y border-neutral-300 py-4 group"
              >
                <span className="inline-flex items-center gap-2 font-display text-2xl text-neutral-900" style={{ fontWeight: 700 }}>
                  <FileText size={20} className="text-neutral-500" /> Transcript
                </span>
                <span className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-500">
                  {showTranscript ? "Hide" : "Read"}
                  <ChevronDown size={18} className={`transition-transform duration-300 ${showTranscript ? "rotate-180" : ""}`} />
                </span>
              </button>
              {showTranscript && (
                <div className="mt-6 flex flex-col gap-5" data-testid="transcript-body">
                  {episode.transcript.map((line, i) => (
                    <div key={`${i}-${line.speaker}`}>
                      <p className="font-sans-ui text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">{line.speaker}</p>
                      <p className="font-display text-lg text-neutral-800 leading-relaxed">{line.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Guest sidebar */}
        <aside>
          <div className="bg-white/60 border border-neutral-200 rounded-lg p-6 sticky top-28">
            <p className="font-sans-ui text-xs uppercase tracking-[0.2em] text-neutral-500">The Guest</p>
            <h4 className="font-display text-2xl text-neutral-900 mt-2" style={{ fontWeight: 700 }}>{episode.guestName}</h4>
            <p className="font-display italic text-neutral-600 mt-1">{episode.guestRole}</p>
            <p className="font-display text-neutral-700 leading-relaxed mt-4">{episode.guestBio}</p>
          </div>
        </aside>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-8 border-t border-neutral-300">
          <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8">More in {nice(episode.category)}</p>
          <div className="grid sm:grid-cols-3 gap-8">
            {related.map((ep) => (
              <Link key={ep.id} to={`/episode/${ep.id}`} className="group">
                <div className="relative w-full aspect-video overflow-hidden rounded-md">
                  <img src={ep.thumbnail} alt={ep.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <h3 className="font-display text-xl text-neutral-900 mt-3" style={{ fontWeight: 700 }}>{ep.title}</h3>
                <p className="font-display italic text-neutral-600">{ep.guest}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {relatedArticles.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-8 border-t border-neutral-300" data-testid="episode-related-articles">
          <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8">Read more on this topic</p>
          <div className="grid sm:grid-cols-3 gap-8">
            {relatedArticles.map((post) => (
              <Link key={post.slug} to={`/article/${post.slug}`} className="group" data-testid={`episode-related-article-${post.slug}`}>
                <div className="relative w-full aspect-video overflow-hidden rounded-md">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <p className="font-sans-ui text-xs uppercase tracking-[0.18em] text-neutral-500 mt-3">{post.readTime}</p>
                <h3 className="font-display text-xl text-neutral-900 mt-1 leading-snug group-hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>{post.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EpisodePage;
